import React from 'react';
import { Info, Ruler, Calculator, PenTool, Lightbulb, AlertTriangle, Home, Hammer } from 'lucide-react';

/**
 * Компонент с информацией о расчете площади и ремонте
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-fuchsia-500" />
        Всё о расчете площади помещения: типы, методы и советы
      </h2>
      
      <div className="prose prose-fuchsia dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Ruler className="w-5 h-5 text-fuchsia-500" />
          Зачем точно рассчитывать площадь помещения?
        </h3>
        <p>
          <strong>Точный расчет площади помещения</strong> имеет ключевое значение для различных целей:
        </p>
        <ul>
          <li><strong>Планирование ремонта:</strong> Позволяет рассчитать точное количество необходимых материалов, что помогает избежать лишних трат или нехватки.</li>
          <li><strong>Дизайн интерьера:</strong> Необходим для правильного размещения мебели, выбора оптимального масштаба предметов и создания гармоничного пространства.</li>
          <li><strong>Юридические вопросы:</strong> Требуется для договоров купли-продажи, регистрации недвижимости, кадастровых документов, налогообложения.</li>
          <li><strong>Оценка стоимости:</strong> Используется для определения рыночной стоимости недвижимости.</li>
          <li><strong>Коммунальные платежи:</strong> Влияет на расчет коммунальных услуг (отопление, уборка МОП и т.д.).</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-fuchsia-500" />
          Какие бывают типы площадей и как правильно их измерять?
        </h3>
        <p>
          В России существует несколько типов площадей, которые используются в различных контекстах:
        </p>
        <ul>
          <li>
            <strong>Общая площадь</strong> — сумма площадей всех частей помещения, включая площади лоджий, балконов, веранд, террас с понижающими коэффициентами.
            <ul>
              <li>Лоджии считаются с коэффициентом 0,5</li>
              <li>Балконы и террасы — с коэффициентом 0,3</li>
              <li>Веранды и холодные кладовые — с коэффициентом 1,0</li>
            </ul>
          </li>
          <li><strong>Жилая площадь</strong> — сумма площадей жилых комнат без учета вспомогательных помещений (кухня, санузел, коридор и т.д.).</li>
          <li><strong>Полезная площадь</strong> — площадь всех помещений, исключая конструктивные элементы (стены, колонны, вентиляционные шахты и т.д.).</li>
          <li><strong>Площадь застройки</strong> — площадь горизонтального сечения здания по внешнему обводу наружных стен на уровне цоколя.</li>
        </ul>

        <div className="p-4 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 my-6">
          <h4 className="text-fuchsia-700 dark:text-fuchsia-300 font-medium">Согласно российским стандартам</h4>
          <p className="text-fuchsia-600 dark:text-fuchsia-400 text-sm mb-0">
            Расчет производится по внутренним размерам помещений (от поверхности стен/перегородок). Для частных домов и нежилых зданий могут действовать другие нормы.
            Основными стандартами являются СП 54.13330.2016 и ЖК РФ Статья 15, которые регламентируют правила измерения площадей.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <PenTool className="w-5 h-5 text-fuchsia-500" />
          Методы расчета площади для разных форм помещений
        </h3>
        <p>
          Расчёт площади зависит от формы помещения. Вот наиболее распространенные методы:
        </p>
        <ul>
          <li><strong>Для прямоугольных помещений:</strong> Длина × Ширина</li>
          <li><strong>Для квадратных помещений:</strong> Сторона × Сторона</li>
          <li><strong>Для Г-образных и сложных помещений:</strong> Разбиение на прямоугольники и сложение их площадей</li>
          <li><strong>Для комнаты со скошенным углом:</strong> Площадь прямоугольника – Площадь треугольного скоса</li>
          <li><strong>Для помещений с колоннами:</strong> Площадь комнаты – Площадь колонн</li>
          <li><strong>Для круглых помещений:</strong> π × (Диаметр/2)² или π × Радиус²</li>
          <li><strong>Для комнат с эркерами и нишами:</strong> Основная площадь + Площадь эркера/ниши</li>
        </ul>
        <p>
          Для стен площадь рассчитывается как произведение периметра помещения на высоту, за вычетом площади проемов (окна, двери). 
          Для потолка площадь совпадает с площадью пола, но при наличии многоуровневых конструкций нужно учитывать дополнительную площадь.
        </p>

        <h3 className="flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-fuchsia-500" />
          Расчет материалов для ремонта
        </h3>
        <p>
          На основе площади можно рассчитать необходимое количество различных отделочных материалов:
        </p>
        <ul>
          <li>
            <strong>Напольные покрытия</strong>
            <ul>
              <li>Ламинат: Площадь + 5-7% запаса (стандартная упаковка ~2,5 м²)</li>
              <li>Линолеум: Площадь + 5-10% запаса на подгонку рисунка</li>
              <li>Паркет: Площадь + 10% запаса</li>
              <li>Плитка: Площадь + 10-15% запаса на подрезку и брак</li>
              <li>Ковролин: Площадь с учетом рисунка и направления ворса</li>
            </ul>
          </li>
          <li>
            <strong>Настенные материалы</strong>
            <ul>
              <li>Обои: (Периметр ÷ Ширина рулона) × Высота стен + запас на подгонку рисунка</li>
              <li>Краска: Площадь стен × Расход краски (указан на упаковке) × Количество слоев</li>
              <li>Плитка: Площадь отделываемой поверхности + 10-15% запаса</li>
            </ul>
          </li>
          <li>
            <strong>Потолочные материалы</strong>
            <ul>
              <li>Натяжной потолок: Площадь потолка (производители обычно сами делают все замеры)</li>
              <li>Краска: Площадь потолка × Расход краски × Количество слоев</li>
              <li>Потолочная плитка: Площадь потолка ÷ Площадь одной плитки + 5% запаса</li>
            </ul>
          </li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Рекомендации при покупке материалов
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Всегда приобретайте материалы с запасом (5-15% в зависимости от типа)</li>
            <li>Учитывайте рисунок и направление укладки материала</li>
            <li>Для обоев из одной партии совпадают оттенки, для разных — могут отличаться</li>
            <li>Проверяйте соответствие артикулов и партий при покупке плитки</li>
            <li>Тщательно измеряйте сложные участки (ниши, эркеры, колонны и т.д.)</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Home className="w-5 h-5 text-fuchsia-500" />
          Особенности российских помещений и их влияние на расчеты
        </h3>
        <p>
          В России есть ряд особенностей жилых помещений, которые следует учитывать при расчетах:
        </p>
        <ul>
          <li><strong>Технические нормы:</strong> ГОСТ, СНиП и СП регламентируют минимальные площади помещений, высоту потолков, соотношение площади окон к площади пола.</li>
          <li><strong>Типовые серии домов:</strong> В разных сериях советских и постсоветских домов (хрущевки, брежневки, сталинки, панельные дома разных серий) есть типовые планировки с известными особенностями.</li>
          <li><strong>Несущие конструкции:</strong> В панельных домах не все стены можно сносить, что влияет на возможности перепланировки.</li>
          <li><strong>Сезонные колебания:</strong> Из-за климатических особенностей материалы могут расширяться и сжиматься, что требует учёта температурных зазоров.</li>
          <li><strong>Отопление:</strong> Наличие радиаторов отопления требует учёта их расположения при планировании мебели и отделки.</li>
          <li><strong>Утепление:</strong> В северных регионах может требоваться дополнительное утепление, которое уменьшает полезную площадь.</li>
        </ul>
        <p>
          В современных новостройках часто используются свободные планировки, где внутренние перегородки не являются несущими, что даёт больше свободы в организации пространства. В таких случаях важно учитывать расположение вентиляционных шахт, стояков и несущих колонн при планировании.
        </p>

        <h3 className="flex items-center gap-2">
          <Hammer className="w-5 h-5 text-fuchsia-500" />
          Практические советы по измерению и ремонту
        </h3>
        <p>
          Вот несколько практических рекомендаций, которые помогут вам в процессе измерения и планирования ремонта:
        </p>
        <ol>
          <li><strong>Используйте качественный инструментарий:</strong> Лазерный дальномер или рулетка с хорошей фиксацией значительно упрощают замеры и повышают точность.</li>
          <li><strong>Документируйте измерения:</strong> Делайте подробные записи и схемы, добавляйте фотографии с указанием размеров.</li>
          <li><strong>Учитывайте кривизну стен:</strong> В старых домах стены редко бывают идеально ровными, измеряйте в нескольких местах.</li>
          <li><strong>Проверяйте углы:</strong> Не все углы в помещениях прямые, особенно в старом фонде. Используйте угольник для проверки.</li>
          <li><strong>Измеряйте на нужной высоте:</strong> Стены могут отклоняться от вертикали, поэтому измеряйте на той высоте, где будет производиться монтаж/укладка.</li>
          <li><strong>Обратите внимание на пороги и переходы:</strong> При укладке напольных покрытий важно учесть разницу уровней между комнатами.</li>
          <li><strong>Взаимодействуйте с профессионалами:</strong> Для сложных форм помещений или при наличии архитектурных особенностей консультируйтесь со специалистами.</li>
        </ol>

        <div className="p-4 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 my-6">
          <h4 className="text-fuchsia-700 dark:text-fuchsia-300 font-medium">В каких случаях лучше обратиться к профессионалам?</h4>
          <p className="text-fuchsia-600 dark:text-fuchsia-400 text-sm mb-0">
            Самостоятельные измерения подходят для простых помещений и базового планирования. 
            Однако для сложной отделки, перепланировки, дизайн-проектов или когда требуется высокая точность (например, для изготовления мебели на заказ), 
            рекомендуется привлекать специалистов: дизайнеров, архитекторов или технических замерщиков, 
            которые используют профессиональные инструменты и технологии, включая 3D-лазерное сканирование.
          </p>
        </div>

        <h3>Распространённые ошибки при замерах и расчетах</h3>
        <ul>
          <li><strong>Неучёт толщины материалов</strong> — важно помнить, что отделка уменьшает полезное пространство. Например, утепление стен пенопластом может "съесть" 5-10 см от площади комнаты.</li>
          <li><strong>Игнорирование неровностей</strong> — криволинейность стен может значительно повлиять на расход материалов и сложность монтажа.</li>
          <li><strong>Ошибки в расчете обоев</strong> — неправильный учет подгонки рисунка может привести к нехватке материала.</li>
          <li><strong>Недостаточный запас</strong> — недооценка количества отходов при укладке или монтаже.</li>
          <li><strong>Неучет особенностей конкретного помещения</strong> — например, не учитывается необходимость герметизации в помещениях с повышенной влажностью.</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор площади помещения поможет вам точно рассчитать необходимые параметры для различных форм комнат, 
          предоставит информацию по количеству материалов и примерную стоимость, что существенно упростит планирование ремонта!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;